// ===================================
// UPDATED LAYOUT WITH BREADCRUMB SUPPORT - src/components/Layout.tsx
// ===================================
import React, { useState } from 'react';
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, 
  X, 
  BarChart3, 
  FileText, 
  Activity, 
  Settings,
  ChevronRight
} from 'lucide-react';
import ProfileMenu from './ProfileMenu';

const Layout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Navigation items
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    { name: 'Topics', href: '/topics', icon: FileText },
    { name: 'Health Check', href: '/health', icon: Activity },
  ];

  // Generate breadcrumbs based on current route
  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [
      { name: 'Action Console', href: '/dashboard', isClickable: true }
    ];

    if (path.includes('/topics')) {
      breadcrumbs.push({ name: 'Topics', href: '/topics', isClickable: true });
      
      if (params.topicId) {
        breadcrumbs.push({ 
          name: `Topic ${params.topicId} Actions`, 
          href: `/topics/${params.topicId}/actions`,
          isClickable: false // Current page
        });
      }
    } else if (path.includes('/health')) {
      breadcrumbs.push({ name: 'Health Check', href: '/health', isClickable: false });
    } else if (path === '/dashboard') {
      // Dashboard is already in breadcrumbs as root
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-gray-900 text-sm truncate">Action</span>
                <span className="font-semibold text-gray-900 text-sm truncate">Console</span>
              </div>
            </div>
            <button 
              className="lg:hidden p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                (item.href === '/topics' && location.pathname.startsWith('/topics'));
              
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </nav>

          {/* Phase III Badge */}
          <div className="px-4 pb-4 flex-shrink-0">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                <span className="text-xs font-medium text-blue-800 truncate">Phase III Active</span>
              </div>
              <p className="text-xs text-blue-600 mt-1 line-clamp-2">
                Actions CRUD with Salesforce integration
              </p>
            </div>
          </div>

          {/* User Profile Section */}
          {user && (
            <div className="border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.role_name}</p>
                </div>
              </div>
              <ProfileMenu compact />
            </div>
          )}
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex-shrink-0">
          <div className="flex items-center justify-between h-full px-4 lg:px-6 max-w-full">
            <button
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600 rounded -ml-2 flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            
            {/* Enhanced Breadcrumb Navigation */}
            <div className="hidden sm:block min-w-0 flex-1">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-4 min-w-0">
                  {breadcrumbs.map((crumb, index) => (
                    <li key={crumb.href} className="min-w-0">
                      <div className="flex items-center min-w-0">
                        {index > 0 && (
                          <ChevronRight className="h-4 w-4 text-gray-400 mr-4 flex-shrink-0" />
                        )}
                        {crumb.isClickable ? (
                          <button
                            onClick={() => navigate(crumb.href)}
                            className="text-sm text-gray-500 hover:text-gray-700 transition-colors truncate"
                          >
                            {crumb.name}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-900 font-medium truncate">
                            {crumb.name}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>

            {/* Mobile breadcrumb */}
            <div className="sm:hidden min-w-0 flex-1">
              <span className="text-sm text-gray-900 font-medium truncate block">
                {breadcrumbs[breadcrumbs.length - 1]?.name || 'Action Console'}
              </span>
            </div>
            
            {/* Right side - could add search, notifications, etc. */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Future: Add global search, notifications, etc. */}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6 max-w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;